import { Box, Input, InputGroup, InputLeftElement, Flex, useColorModeValue } from "@chakra-ui/react"
import { faSearch } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Fuse, { FuseOptionKey } from "fuse.js"
import React, { useEffect, useState } from "react"

export interface FuzzyFindWrapperProps<T> {
    data: T[]
    RenderComponent: React.FC<{ data: T } & any>
    renderComponentProps: any
}

interface FuzzySearchProps<T> {
    data: T[],
    RenderComponent: React.FC<{ data: T } & any>
    RenderWrapper: React.FC<FuzzyFindWrapperProps<T>>
    renderComponentProps: any
    searchKeys: Array<keyof T>
    children: React.ReactNode
    headerChildren?: React.ReactNode
    query?: string
}

const FuzzyFinder = <T,>({ data, RenderComponent, renderComponentProps, searchKeys, children, RenderWrapper, headerChildren, query }: FuzzySearchProps<T>) => {
    const isControlled = query !== undefined
    const [localQuery, setLocalQuery] = useState("")
    const [searchedData, setSearchedData] = useState<T[]>(data)
    
    const fuse = new Fuse(data, {
        keys: searchKeys as FuseOptionKey<T>[],
        threshold: 0.3
    })

    const activeQuery = isControlled ? query : localQuery

    useEffect(() => {
        if (activeQuery && activeQuery !== '') {
            setSearchedData(fuse.search(activeQuery).map(result => result.item))
        } else {
            setSearchedData(data)
        }
    }, [activeQuery, data])

    return (
        <Flex direction="column" w='100%' h='100%' maxH='100%' gap={6}>
            {(!isControlled || headerChildren) && (
				<Box
					bg={useColorModeValue("white", "gray.800")}
					p={4}
					borderRadius="2xl"
					boxShadow="sm"
					border="1px solid"
					borderColor={useColorModeValue("gray.100", "gray.700")}
				>
					{headerChildren}
                    {!isControlled && (
                        <InputGroup size="sm" mt={headerChildren ? 4 : 0}>
                            <InputLeftElement pointerEvents="none" color="gray.400">
                                <FontAwesomeIcon icon={faSearch} />
                            </InputLeftElement>
                            <Input
                                placeholder="Search..."
                                value={localQuery}
                                onChange={(e) => setLocalQuery(e.target.value)}
                                borderRadius="md"
                                bg={useColorModeValue('gray.50', 'gray.700')}
                            />
                        </InputGroup>
                    )}
				</Box>
			)}

            <Box flex={1} overflowX='auto' overflowY='auto' minH={0} px={1} pb={4}>
                <RenderWrapper data={searchedData} RenderComponent={RenderComponent} renderComponentProps={renderComponentProps} />
            </Box>
            {children}
        </Flex>
    )
}

export default FuzzyFinder
